import {
  MyFoodsPageFilterSidebar,
  type MyFoodsPageFilterSidebarProps,
} from './MyFoodsPageFilterSidebar';

import {
  useGetRankQuery,
  useGetAllFoodQuery,
} from '@viome/viome-base/src/usecase/Nutrition/NutritionQuery';

import type { GetFoodRankResponse } from '@viome/viome-base/src/usecase/Nutrition/Nutrition.model';

import { sortData } from '@/utils/sortData';
import { NavBar } from '@design-system/NavBar';
import { Button } from '@design-system/Button';
import { Skeleton } from '@design-system/Skeleton';
import { useSearchParams } from 'react-router-dom';
import { StickyNavBar } from '@design-system/StickyNavBar';
import { MyFoodsPageFoodsGrid } from './MyFoodsPageFoodsGrid';
import { ReturnButton } from '@/components/shared/ReturnButton';
import { StatusIndicator } from '@design-system/StatusIndicator';
import { SearchBarTrigger } from '@/components/shared/SearchBar';
import { ScrollArea, ScrollBar } from '@design-system/ScrollArea';
import { BookmarkButton } from '@/components/shared/BookmarkButton';
import { FILTER, DISPLAY, type DisplayMode } from './MyFoodsPage.model';
import { ToggleGroup, ToggleGroupItem } from '@design-system/ToggleGroup';
import { MyFoodsPageDisplayModeButton } from './MyFoodsPageDisplayModeButton';
import { EventTracker } from '@/utils';

const renderFilterOptions = (
  response?: GetFoodRankResponse,
  isLoading?: boolean,
) => {
  if (isLoading) {
    return Array.from({ length: 4 }).map((_, index) => (
      <Skeleton
        key={`food-skeleton-${index}`}
        data-testid={`myFoods-rankFilterSkeleton-${index}`}
        className="h-9 w-24 rounded-lg"
      />
    ));
  }

  if (!response?.length) return null;

  const renderedFilterOptions = sortData(response).map((option, index) => (
    <ToggleGroupItem
      key={option.id}
      data-testid={`myFoods-rankFilter-${index}`}
      value={String(option.id)}
      className="pr-4 pl-4 [&_svg]:size-5 h-9"
    >
      <StatusIndicator color={option.color} size="sm" />
      {option.title}
    </ToggleGroupItem>
  ));

  return renderedFilterOptions;
};

const parseTypeFilterValues = (rawValue?: string | null) => {
  if (!rawValue) return;

  const parsedValue = rawValue.split('#').reduce(
    (acc, encodedValue) => {
      const [rawFilterId, rawFilterValues] = encodedValue.split(':');

      if (!rawFilterValues) return acc;

      const filterId = Number(rawFilterId);
      const values = rawFilterValues.split(';');

      if (Number.isNaN(filterId)) return acc;

      acc[filterId] = values;

      return acc;
    },
    {} as Record<number, string[]>,
  );

  return parsedValue;
};

const parseRankFilterValues = (rawValues?: string[] | null) => {
  if (!Array.isArray(rawValues)) return;

  const parsedValues = rawValues.reduce((acc, rawValue) => {
    const parsedValue = Number(rawValue);

    if (!Number.isNaN(parsedValue)) acc.push(parsedValue);

    return acc;
  }, [] as number[]);

  return parsedValues;
};

const parseSubFilters = (ranks?: number[], bookmarked?: boolean) => {
  const filters = [] as number[];

  if (Array.isArray(ranks)) filters.push(...ranks);
  if (bookmarked) filters.push(0);

  return filters;
};

export const MyFoodsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const filterParams = Object.fromEntries(searchParams.entries());

  const rankFilter = filterParams[FILTER.RANK];
  const typeFilter = filterParams[FILTER.TYPE];
  const bookmarkedFilter = filterParams[FILTER.BOOKMARKED];
  const displayModeFilter = filterParams[FILTER.DISPLAY] ?? DISPLAY.GRID;
  const isFiltered = [rankFilter, typeFilter, bookmarkedFilter].some(Boolean);

  const getRankQuery = useGetRankQuery();

  const isBookmarked = bookmarkedFilter === 'true';
  const rawRankFilters = rankFilter?.split(',') ?? [];
  const parsedRankFilters = parseRankFilterValues(rawRankFilters);
  const parsedTypeFilters = parseTypeFilterValues(typeFilter);

  const getAllFoodQuery = useGetAllFoodQuery(
    parsedTypeFilters,
    parseSubFilters(parsedRankFilters, isBookmarked),
  );

  const foodsAmount = getAllFoodQuery.data?.length ?? 0;

  const renderedFilterOptions = renderFilterOptions(
    getRankQuery.data,
    getRankQuery.isLoading,
  );

  const handleOnToggleDisplayMode = () => {
    setSearchParams((params) => {
      const value =
        displayModeFilter === DISPLAY.LIST ? DISPLAY.GRID : DISPLAY.LIST;
      EventTracker.trackEvent('nutrition', `nutrition_myFoods_${value}View`);

      params.set(FILTER.DISPLAY, value);

      return params;
    });
  };

  const handleOnToggleBookmarkedFilter = () => {
    EventTracker.trackEvent('nutrition', 'tap_nutrition_myFoods_chip_saved');
    setSearchParams((params) => {
      const value = isBookmarked ? 'false' : 'true';

      params.set(FILTER.BOOKMARKED, value);

      return params;
    });
  };

  const handleOnChangeRankFilter = (value: string[]) => {
    setSearchParams((params) => {
      EventTracker.trackEvent('nutrition', 'nutrition_myFoods_chip');
      const filterValue = value?.join(',');
      if (!filterValue) {
        params.delete(FILTER.RANK);
      } else {
        params.set(FILTER.RANK, filterValue);
      }

      return params;
    });
  };

  const handleOnResetFilters = () => {
    EventTracker.trackEvent('nutrition', 'nutrition_myFoods_reset');
    setSearchParams((params) => {
      params.delete(FILTER.TYPE);
      params.delete(FILTER.RANK);
      params.delete(FILTER.BOOKMARKED);

      return params;
    });
  };

  const handleOnSubmitTypeFilters: MyFoodsPageFilterSidebarProps['onSubmit'] = (
    filterValue,
  ) => {
    if (!filterValue) return;

    const encodedFilterValues = Object.entries(filterValue).map(
      ([name, value]) => `${name}:${value?.join(';')}`,
    );

    const joinedFilterValues = encodedFilterValues.join(',');

    setSearchParams((params) => {
      if (!joinedFilterValues) {
        params.delete(FILTER.TYPE);
      } else {
        params.set(FILTER.TYPE, joinedFilterValues);
      }

      return params;
    });
  };

  return (
    <section data-testid="myFoods-page" className="grid gap-6 page-container">
      <NavBar data-testid="myFoods-navbar">
        <SearchBarTrigger
          data-testid="myFoods-searchBar"
          className="md:max-w-80 w-full"
          placeholder="Search in Foods"
        />
      </NavBar>

      <span 
        data-testid="myFoods-header"
        className="flex flex-row items-center"
      >
        <h1 
          data-testid="myFoods-title"
          className="typography-h1 md:typography-d1"
        >
          My Foods
        </h1>

        <MyFoodsPageDisplayModeButton
          data-testid="myFoods-displayModeButtonMobile"
          className="lg:hidden"
          onClick={handleOnToggleDisplayMode}
          displayMode={displayModeFilter as DisplayMode}
        />
      </span>

      <StickyNavBar data-testid="myFoods-stickyNavBar">
        <div className="justify-between page-container !py-0 flex flex-row items-center">
          <ReturnButton data-testid="myFoods-returnButton" />
          <span 
            data-testid="myFoods-stickyTitle"
            className="text-primary typography-h3"
          >
            My Foods Page
          </span>
          <SearchBarTrigger 
            data-testid="myFoods-stickySearchBar"
            variant="compact" 
          />
        </div>
      </StickyNavBar>

      <ScrollArea data-testid="myFoods-filtersScrollArea">
        <div 
          data-testid="myFoods-filtersContainer"
          className="flex flex-row items-center gap-2 pb-3"
        >
          <MyFoodsPageFilterSidebar
            data-testid="myFoods-typeFilterSidebar"
            initialValue={parsedTypeFilters}
            onSubmit={handleOnSubmitTypeFilters}
          />

          <BookmarkButton
            data-testid="myFoods-bookmarkFilter"
            size="default"
            variant="toggle"
            data-focus={isBookmarked}
            bookmarked
            onClick={handleOnToggleBookmarkedFilter}
            className="typography-body h-9 pr-4 pl-3 text-primary [&_svg]:size-5"
          >
            Saved
          </BookmarkButton>

          <ToggleGroup
            data-testid="myFoods-rankFilterGroup"
            type="multiple"
            variant="outline"
            value={rawRankFilters}
            className="flex-nowrap"
            onValueChange={handleOnChangeRankFilter}
          >
            {renderedFilterOptions}
          </ToggleGroup>

          <MyFoodsPageDisplayModeButton
            data-testid="myFoods-displayModeButtonDesktop"
            className="max-lg:hidden"
            onClick={handleOnToggleDisplayMode}
            displayMode={displayModeFilter as DisplayMode}
          />
        </div>

        <ScrollBar 
          data-testid="myFoods-filtersScrollBar"
          orientation="horizontal" 
        />
      </ScrollArea>

      <div 
        data-testid="myFoods-resultsInfo"
        className="w-full typography-body text-secondary flex flex-row gap-1 items-center"
      >
        {isFiltered && 'Filtered - '}
        Showing
        <span data-testid="myFoods-resultsCount">
          {getAllFoodQuery.isLoading ? (
            <Skeleton 
              data-testid="myFoods-resultsCountSkeleton"
              className="h-5 w-16" 
            />
          ) : (
            foodsAmount
          )}
        </span>
        foods
        {isFiltered && (
          <Button
            data-testid="myFoods-resetFiltersButton"
            variant="flat"
            onClick={handleOnResetFilters}
            className="ml-auto typography-title text-primary p-0 h-auto"
          >
            Reset
          </Button>
        )}
      </div>

      <MyFoodsPageFoodsGrid
        data-testid="myFoods-foodsGrid"
        foodRank={getRankQuery.data}
        foods={getAllFoodQuery.data}
        isLoading={getAllFoodQuery.isLoading}
        displayMode={displayModeFilter as DisplayMode}
      />
    </section>
  );
};
